import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Heart, ShoppingCart, Menu, Globe, ChevronDown, User, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useApp } from '../context/AppContext';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { currencies, languages, categories } from '../mock';

export default function Header() {
  const navigate = useNavigate();
  const { cart, currency, setCurrency, language, setLanguage } = useApp();
  const [q, setQ] = useState('');
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const cartCount = cart.reduce((n, p) => n + p.qty, 0);

  const onSearch = (e) => {
    e.preventDefault();
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setMobileSearchOpen(false);
  };

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-gray-100">
      {/* Desktop */}
      <div className="hidden md:block">
        <div className="container-k flex items-center gap-6 h-16">
          <Link to="/" className="flex items-center gap-1">
            <span className="text-2xl font-extrabold tracking-tight" style={{ color: '#ff5b00' }}>xlook</span>
            <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: '#ff5b00' }} />
          </Link>

          <form onSubmit={onSearch} className="flex-1 max-w-2xl">
            <div className="flex items-center h-11 rounded-full border border-gray-200 bg-gray-50 hover:border-gray-300 focus-within:border-gray-400 px-4 transition-colors">
              <Search className="w-4 h-4 text-gray-500" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search for destinations or activities"
                className="flex-1 ml-3 bg-transparent outline-none text-sm placeholder:text-gray-500"
              />
              <Button type="submit" size="sm" className="rounded-full bg-xlook-orange text-white hover:opacity-90 h-8 px-4">Search</Button>
            </div>
          </form>

          <nav className="flex items-center gap-4 text-sm">
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-gray-700 hover:text-gray-900">
                <Globe className="w-4 h-4" /> {language} / {currency} <ChevronDown className="w-3 h-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <div className="px-2 py-1.5 text-xs font-semibold text-gray-500">Language</div>
                {languages.map((l) => (
                  <DropdownMenuItem key={l} onClick={() => setLanguage(l)}>{l}</DropdownMenuItem>
                ))}
                <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 mt-1">Currency</div>
                <div className="grid grid-cols-3 gap-1 p-1">
                  {currencies.map((c) => (
                    <button key={c} onClick={() => setCurrency(c)} className={`text-xs py-1.5 rounded-md border ${currency === c ? 'border-xlook-orange text-xlook-orange' : 'border-gray-200 text-gray-700'}`}>{c}</button>
                  ))}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to="/wishlist" className="text-gray-700 hover:text-gray-900 flex items-center gap-1"><Heart className="w-4 h-4" /> Wishlist</Link>
            <Link to="/checkout" className="text-gray-700 hover:text-gray-900 flex items-center gap-1 relative">
              <ShoppingCart className="w-4 h-4" /> Cart
              {cartCount > 0 && <span className="absolute -top-2 -right-3 bg-xlook-orange text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-semibold">{cartCount}</span>}
            </Link>
            <button className="text-gray-700 hover:text-gray-900 flex items-center gap-1"><User className="w-4 h-4" /> Sign in</button>
          </nav>
        </div>
        {/* Category row */}
        <div className="border-t border-gray-100">
          <div className="container-k flex items-center gap-6 h-11 overflow-x-auto hide-scrollbar">
            {categories.map((c) => (
              <Link key={c.id} to={`/search?category=${c.id}`} className="text-sm text-gray-700 hover:text-xlook-orange whitespace-nowrap">{c.name}</Link>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile */}
      <div className="md:hidden">
        <div className="flex items-center gap-2 px-4 h-14">
          <Sheet>
            <SheetTrigger asChild>
              <button className="p-2 -ml-2"><Menu className="w-6 h-6" /></button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[85%] p-0">
              <div className="p-4 border-b">
                <div className="text-xl font-extrabold" style={{ color: '#ff5b00' }}>xlook</div>
              </div>
              <div className="p-4 space-y-1">
                <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Categories</div>
                {categories.map((c) => (
                  <Link key={c.id} to={`/search?category=${c.id}`} className="block py-2.5 text-sm text-gray-800 border-b border-gray-50">{c.name}</Link>
                ))}
                <div className="text-xs font-semibold text-gray-500 uppercase mt-4 mb-2">Account</div>
                <Link to="/wishlist" className="block py-2.5 text-sm text-gray-800">Wishlist</Link>
                <Link to="/checkout" className="block py-2.5 text-sm text-gray-800">Cart ({cartCount})</Link>
                <button className="block py-2.5 text-sm text-gray-800 w-full text-left">Sign in</button>
              </div>
            </SheetContent>
          </Sheet>

          <Link to="/" className="text-xl font-extrabold" style={{ color: '#ff5b00' }}>xlook</Link>

          <button onClick={() => setMobileSearchOpen(true)} className="flex-1 flex items-center gap-2 ml-2 h-9 px-3 rounded-full bg-gray-100 text-gray-500 text-sm">
            <Search className="w-4 h-4" /> Search
          </button>
          <Link to="/checkout" className="relative p-2">
            <ShoppingCart className="w-5 h-5" />
            {cartCount > 0 && <span className="absolute top-0 right-0 bg-xlook-orange text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-semibold">{cartCount}</span>}
          </Link>
        </div>

        {mobileSearchOpen && (
          <div className="fixed inset-0 bg-white z-50 animate-in fade-in">
            <form onSubmit={onSearch} className="flex items-center gap-2 p-3 border-b">
              <button type="button" onClick={() => setMobileSearchOpen(false)}><X className="w-5 h-5" /></button>
              <div className="flex-1 flex items-center h-10 rounded-full bg-gray-100 px-4">
                <Search className="w-4 h-4 text-gray-500" />
                <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search destinations or activities" className="flex-1 ml-2 bg-transparent outline-none text-sm" />
              </div>
              <button type="submit" className="text-sm font-semibold text-xlook-orange">Search</button>
            </form>
            <div className="p-4">
              <div className="text-xs font-semibold text-gray-500 uppercase mb-3">Popular searches</div>
              <div className="flex flex-wrap gap-2">
                {['Tokyo Disney', 'Universal Studios', 'Bangkok', 'Bali Tour', 'Singapore', 'Seoul', 'Hong Kong', 'Cooking Class'].map((t) => (
                  <button key={t} onClick={() => { setQ(t); navigate(`/search?q=${encodeURIComponent(t)}`); setMobileSearchOpen(false); }} className="px-3 py-1.5 rounded-full bg-gray-100 text-sm text-gray-700">{t}</button>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="border-t border-gray-100 overflow-x-auto hide-scrollbar">
          <div className="flex gap-5 px-4 h-10 items-center">
            {categories.map((c) => (
              <Link key={c.id} to={`/search?category=${c.id}`} className="text-xs text-gray-700 whitespace-nowrap">{c.name}</Link>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
}
